import {
  Box,
  Collapse,
  Drawer,
  Group,
  Paper,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import {
  IconChevronDown,
  IconChevronRight,
  IconMenu2,
} from '@tabler/icons-react'
import { Link, useRouteContext, useRouter } from '@tanstack/react-router'
import { useState } from 'react'
import { extractRoles } from '@/utils/routeGuards'
import { CkdLogo } from './logo'
import { MenuGroup, MenuItemWithAllowedRoles, Menus, MenuSection } from './menu'

interface MobileMenuProps {
  menus: Menus
}

export function MobileMenu({ menus }: MobileMenuProps) {
  const [opened, { open, close }] = useDisclosure(false)
  const [openedGroups, setOpenedGroups] = useState<Record<string, boolean>>({})
  const router = useRouter()
  const context = useRouteContext({ from: '/_auth' })

  const toggleGroup = (groupLabel: string) => {
    setOpenedGroups((prev) => {
      // Close all other groups and toggle the current one
      const newState: Record<string, boolean> = {}
      for (const key in prev) {
        newState[key] = false
      }
      newState[groupLabel] = !prev[groupLabel]
      return newState
    })
  }

  const handleMenuItemClick = (path: string) => {
    router.navigate({ to: path })
    close()
  }

  const renderMenuItem = (item: MenuItemWithAllowedRoles) => {
    let hasPermission = true
    const roles = extractRoles(item.allowedRoles || [])
    if (context.auth && roles) {
      hasPermission = context.auth.hasRequiredRole(roles)
    }

    if (!hasPermission) return null

    return (
      <UnstyledButton
        key={item.path}
        onClick={() => handleMenuItemClick(item.path)}
        p="sm"
        pl="lg"
        w="100%"
        style={{
          borderRadius: 6,
          backgroundColor: 'white',
          border: '1px solid var(--mantine-color-gray-2)',
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          textAlign: 'left',
          '&:hover': {
            backgroundColor: 'var(--mantine-color-blue-0)',
            borderColor: 'var(--mantine-color-blue-3)',
          },
        }}
      >
        <Group gap="sm" align="center" justify="flex-start" w="100%">
          <Box style={{ width: 16, flexShrink: 0 }}>{item.leftSection}</Box>
          <Text size="sm" fw={400} style={{ textAlign: 'left', flex: 1 }}>
            {item.label}
          </Text>
        </Group>
      </UnstyledButton>
    )
  }

  const renderMenuSection = (section: MenuSection) => {
    // Check for permission in each child
    let hasPermission = false
    section.menus.forEach((item) => {
      const roles = extractRoles(item.allowedRoles || [])
      if (context.auth && roles) {
        hasPermission = hasPermission || context.auth.hasRequiredRole(roles)
      }
    })

    if (!hasPermission) return null

    const sectionItems = section.menus.map((item) => renderMenuItem(item))

    return (
      <Box key={section.label || Math.random()} ml="md">
        {section.label && (
          <Box mb="xs" pl="sm">
            <Group gap="xs" align="center" justify="flex-start">
              <Box w={3} h={12} bg="blue.5" style={{ borderRadius: 2 }} />
              <Text size="xs" fw={600} c="dark.7" tt="uppercase" lts={0.5}>
                {section.label}
              </Text>
            </Group>
          </Box>
        )}
        <Stack gap="xs" mb="sm">
          {sectionItems}
        </Stack>
      </Box>
    )
  }

  const renderMenuGroup = (group: MenuGroup) => {
    const sections = group.sections
      .map((section) => renderMenuSection(section))
      .filter((section) => section !== null)

    if (sections.length === 0) return null

    const isGroupOpen = openedGroups[group.label]

    return (
      <Box key={group.label} mb="lg">
        <Paper
          withBorder
          radius="md"
          style={{
            overflow: 'hidden',
            borderColor: isGroupOpen
              ? 'var(--mantine-color-blue-3)'
              : 'var(--mantine-color-gray-3)',
          }}
        >
          <UnstyledButton
            onClick={() => toggleGroup(group.label)}
            p="md"
            w="100%"
            style={{
              backgroundColor: isGroupOpen
                ? 'var(--mantine-color-blue-0)'
                : 'var(--mantine-color-gray-0)',
              borderBottom: isGroupOpen
                ? '1px solid var(--mantine-color-blue-2)'
                : 'none',
              '&:hover': {
                backgroundColor: isGroupOpen
                  ? 'var(--mantine-color-blue-1)'
                  : 'var(--mantine-color-gray-1)',
              },
            }}
          >
            <Group justify="space-between" align="center">
              <Group gap="md" align="center" justify="flex-start">
                <Box
                  style={{
                    color: 'var(--mantine-color-blue-6)',
                    width: 24,
                    textAlign: 'left',
                  }}
                >
                  {group.leftSection}
                </Box>
                <Text
                  fw={600}
                  size="md"
                  c={isGroupOpen ? 'blue.7' : 'dark.8'}
                  style={{ textAlign: 'left' }}
                >
                  {group.label}
                </Text>
              </Group>
              {isGroupOpen ? (
                <IconChevronDown
                  size="1.2rem"
                  style={{ color: 'var(--mantine-color-blue-6)' }}
                />
              ) : (
                <IconChevronRight
                  size="1.2rem"
                  style={{ color: 'var(--mantine-color-gray-6)' }}
                />
              )}
            </Group>
          </UnstyledButton>

          <Collapse in={isGroupOpen}>
            <Box p="md" pt="sm" bg="white">
              <Stack gap="sm">{sections}</Stack>
            </Box>
          </Collapse>
        </Paper>
      </Box>
    )
  }

  const menuGroups = menus
    .map((group) => renderMenuGroup(group))
    .filter((group) => group !== null)

  return (
    <>
      <UnstyledButton
        onClick={open}
        hiddenFrom="md"
        p="xs"
        style={{
          borderRadius: 8,
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          },
        }}
      >
        <IconMenu2 size="1.5rem" color="white" />
      </UnstyledButton>

      <Drawer
        opened={opened}
        onClose={close}
        title={
          <Link to="/" onClick={close}>
            <Group gap="sm">
              <CkdLogo width={40} color="#37AFE1" />
            </Group>
          </Link>
        }
        size="sm"
        position="left"
        styles={{
          content: {
            backgroundColor: 'var(--mantine-color-gray-0)',
          },
        }}
      >
        <Box p="xs">
          <Stack gap="xs">{menuGroups}</Stack>
        </Box>
      </Drawer>
    </>
  )
}
